import styled from "@emotion/styled";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { Button, SubmitButton, TextField } from "@avenue-8/sidekick-ui";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  newPresentationSchema,
  NewPresentationFormSchema,
  buildCreatePresentationPrompt,
} from "./new-presentation-helpers";
import { useCallback } from "react";

const FieldsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  padding-top: 0.5rem;
`;

type NewPresentationDialogProps = {
  open: boolean;
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (message: string) => void;
};
export function NewPresentationDialog(props: NewPresentationDialogProps) {
  const { open, isSubmitting, onClose, onSubmit } = props;

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NewPresentationFormSchema>({
    resolver: yupResolver(newPresentationSchema),
    defaultValues: { title: "", clientName: "", propertyInfo: "" },
    reValidateMode: "onChange",
  });

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    reset();
    onClose();
  }, [isSubmitting, onClose, reset]);

  const handleCreate = (values: NewPresentationFormSchema) => {
    onSubmit(buildCreatePresentationPrompt(values));
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>New CMA Presentation</DialogTitle>
      <DialogContent>
        <FieldsStack>
          <TextField
            label="Presentation title (optional)"
            {...register("title")}
            fieldError={errors.title}
            disabled={isSubmitting}
            hideHelperTextSpace
          />
          <TextField
            label="Client name (optional)"
            {...register("clientName")}
            fieldError={errors.clientName}
            disabled={isSubmitting}
            hideHelperTextSpace
          />
          <TextField
            label="Subject property"
            {...register("propertyInfo")}
            fieldError={errors.propertyInfo}
            disabled={isSubmitting}
            multiline
            rows={2}
            inputProps={{ maxLength: 2000 }}
          />
        </FieldsStack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={handleClose} disabled={isSubmitting}>
          Cancel
        </Button>
        <SubmitButton
          variant="contained"
          onClick={handleSubmit(handleCreate)}
          isLoading={isSubmitting}
          loadingText="Sending..."
        >
          Create
        </SubmitButton>
      </DialogActions>
    </Dialog>
  );
}
